import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Notice the emptiness",
    opening: localizedGenderNode(
      [
      "Today I don't try to fill it.",
      "I only notice the emptiness as it is.",
      "That is enough for now."
    ],
      [
      "Today I don't try to fill it.",
      "I only notice the emptiness as it is.",
      "That is enough for now."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When is this emptiness most felt?",
      "What happens right before it rises?",
      "How does it feel in my body?"
    ],
      [
      "When is this emptiness most felt?",
      "What happens right before it rises?",
      "How does it feel in my body?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Noticing is already a connection."
    ],
      [
      "Noticing is already a connection."
    ]
    ),
    action: localizedGenderNode(
      "one breath with the words \"I am here\".",
      "one breath with the words \"I am here\"."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "What it looks like outside",
    opening: localizedGenderNode(
      [
      "Today I see the gap.",
      "Between what is seen in me and what I feel.",
      "Just to see."
    ],
      [
      "Today I see the gap.",
      "Between what is seen in me and what I feel.",
      "Just to see."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I feel my feet."
    ],
      [
      "I breathe slowly.",
      "I feel my feet."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What does my life look like from the outside?",
      "What feels different inside?",
      "What is hard for me to say about this gap?"
    ],
      [
      "What does my life look like from the outside?",
      "What feels different inside?",
      "What is hard for me to say about this gap?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "The gap doesn't mean I'm fake. It means I'm not alone in this."
    ],
      [
      "The gap doesn't mean I'm fake. It means I'm not alone in this."
    ]
    ),
    action: localizedGenderNode(
      "write one word about the gap.",
      "write one word about the gap."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "When it began",
    opening: localizedGenderNode(
      [
      "Today I look for the beginning of the emptiness.",
      "Without blame.",
      "Just a sign."
    ],
      [
      "Today I look for the beginning of the emptiness.",
      "Without blame.",
      "Just a sign."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "One breath."
    ],
      [
      "I pause for a moment.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When did I first notice it?",
      "What was happening around me then?",
      "What has changed in me since then?"
    ],
      [
      "When did I first notice it?",
      "What was happening around me then?",
      "What has changed in me since then?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Sometimes a small beginning explains a lot."
    ],
      [
      "Sometimes a small beginning explains a lot."
    ]
    ),
    action: localizedGenderNode(
      "give a gentle name to the beginning.",
      "give a gentle name to the beginning."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "What I am missing",
    opening: localizedGenderNode(
      [
      "Today I ask what I truly miss.",
      "Not to obtain.",
      "Just to identify."
    ],
      [
      "Today I ask what I truly miss.",
      "Not to obtain.",
      "Just to identify."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I feel my chest."
    ],
      [
      "I breathe slowly.",
      "I feel my chest."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What do I miss in these moments?",
      "What did I need and not receive?",
      "Which small need is waking up in me?"
    ],
      [
      "What do I miss in these moments?",
      "What did I need and not receive?",
      "Which small need is waking up in me?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "A small need is a beginning of connection."
    ],
      [
      "A small need is a beginning of connection."
    ]
    ),
    action: localizedGenderNode(
      "write one need quietly.",
      "write one need quietly."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "Protections that work hard",
    opening: localizedGenderNode(
      [
      "Today I see my protections.",
      "What they are trying to guard.",
      "Without judging."
    ],
      [
      "Today I see my protections.",
      "What they are trying to guard.",
      "Without judging."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I feel my shoulders."
    ],
      [
      "I pause for a moment.",
      "I feel my shoulders."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What is my main protection against the emptiness?",
      "What is it trying to keep me from feeling?",
      "How does it affect my day?"
    ],
      [
      "What is my main protection against the emptiness?",
      "What is it trying to keep me from feeling?",
      "How does it affect my day?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Even protection is a sign of pain."
    ],
      [
      "Even protection is a sign of pain."
    ]
    ),
    action: localizedGenderNode(
      "tell myself \"I see you\".",
      "tell myself \"I see you\"."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Moments of aliveness",
    opening: localizedGenderNode(
      [
      "Today I look for moments of aliveness.",
      "Even small.",
      "Even brief."
    ],
      [
      "Today I look for moments of aliveness.",
      "Even small.",
      "Even brief."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe.",
      "I feel my hands."
    ],
      [
      "I breathe.",
      "I feel my hands."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When did I feel a drop of aliveness recently?",
      "What was there that helped?",
      "What can I invite 1% more?"
    ],
      [
      "When did I feel a drop of aliveness recently?",
      "What was there that helped?",
      "What can I invite 1% more?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Even a drop of aliveness shifts the direction."
    ],
      [
      "Even a drop of aliveness shifts the direction."
    ]
    ),
    action: localizedGenderNode(
      "remember one such moment.",
      "remember one such moment."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "fear",
    title: "Between emptiness and fatigue",
    opening: localizedGenderNode(
      [
      "Today I separate emptiness from fatigue.",
      "Maybe they are not the same.",
      "I check."
    ],
      [
      "Today I separate emptiness from fatigue.",
      "Maybe they are not the same.",
      "I check."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "One breath."
    ],
      [
      "I pause for a moment.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Am I tired or empty - or both?",
      "What does my body ask for now?",
      "What would I give it if I could?"
    ],
      [
      "Am I tired or empty - or both?",
      "What does my body ask for now?",
      "What would I give it if I could?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "The body sometimes knows before the mind."
    ],
      [
      "The body sometimes knows before the mind."
    ]
    ),
    action: localizedGenderNode(
      "listen to my body for one minute.",
      "listen to my body for one minute."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "need",
    title: "Words I did not say",
    opening: localizedGenderNode(
      [
      "Today I notice what was not said.",
      "That is also part of the emptiness.",
      "I am with myself."
    ],
      [
      "Today I notice what was not said.",
      "That is also part of the emptiness.",
      "I am with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I feel my throat."
    ],
      [
      "I breathe slowly.",
      "I feel my throat."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What have I not said for a long time?",
      "Who did I want to say something to?",
      "What would I say to myself first?"
    ],
      [
      "What have I not said for a long time?",
      "Who did I want to say something to?",
      "What would I say to myself first?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Unsaid words still live in me."
    ],
      [
      "Unsaid words still live in me."
    ]
    ),
    action: localizedGenderNode(
      "write one sentence I did not say.",
      "write one sentence I did not say."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "need",
    title: "A boundary that protects",
    opening: localizedGenderNode(
      [
      "Today I look for a small boundary that protects the heart.",
      "Without shutting down.",
      "Just to protect."
    ],
      [
      "Today I look for a small boundary that protects the heart.",
      "Without shutting down.",
      "Just to protect."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I feel my pulse."
    ],
      [
      "I pause for a moment.",
      "I feel my pulse."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Where do I give too much?",
      "What small boundary would ease me?",
      "How can I say it to myself?"
    ],
      [
      "Where do I give too much?",
      "What small boundary would ease me?",
      "How can I say it to myself?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "A small boundary can return a sense of me."
    ],
      [
      "A small boundary can return a sense of me."
    ]
    ),
    action: localizedGenderNode(
      "one boundary sentence to myself.",
      "one boundary sentence to myself."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "One request",
    opening: localizedGenderNode(
      [
      "Today I form one request for myself.",
      "Not big.",
      "Simple."
    ],
      [
      "Today I form one request for myself.",
      "Not big.",
      "Simple."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe.",
      "I am here."
    ],
      [
      "I breathe.",
      "I am here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What do I truly need right now?",
      "How can I ask for it gently?",
      "What is a small step toward it?"
    ],
      [
      "What do I truly need right now?",
      "How can I ask for it gently?",
      "What is a small step toward it?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "One request can open a door."
    ],
      [
      "One request can open a door."
    ]
    ),
    action: localizedGenderNode(
      "write the request in one sentence.",
      "write the request in one sentence."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "perspective",
    title: "A small thing that nourishes",
    opening: localizedGenderNode(
      [
      "Today I look for one small thing that nourishes me.",
      "Not to change.",
      "Just to return."
    ],
      [
      "Today I look for one small thing that nourishes me.",
      "Not to change.",
      "Just to return."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I feel my belly."
    ],
      [
      "I pause for a moment.",
      "I feel my belly."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What nourishes me even a little?",
      "When did I last do it?",
      "How can I invite it today?"
    ],
      [
      "What nourishes me even a little?",
      "When did I last do it?",
      "How can I invite it today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Something small can remind me that I am alive."
    ],
      [
      "Something small can remind me that I am alive."
    ]
    ),
    action: localizedGenderNode(
      "choose one small nourishing thing.",
      "choose one small nourishing thing."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "strength",
    title: "Sitting with myself",
    opening: localizedGenderNode(
      [
      "Today I sit with myself for a moment.",
      "No need to do.",
      "Just to be."
    ],
      [
      "Today I sit with myself for a moment.",
      "No need to do.",
      "Just to be."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I notice the quiet."
    ],
      [
      "I breathe slowly.",
      "I notice the quiet."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What happens in me when I just sit?",
      "What is hardest for me in this quiet?",
      "What softens me even a little?"
    ],
      [
      "What happens in me when I just sit?",
      "What is hardest for me in this quiet?",
      "What softens me even a little?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Being with myself for a moment is already a small change."
    ],
      [
      "Being with myself for a moment is already a small change."
    ]
    ),
    action: localizedGenderNode(
      "one minute of sitting with a breath.",
      "one minute of sitting with a breath."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "strength",
    title: "A gentle sentence to myself",
    opening: localizedGenderNode(
      [
      "Today I choose a gentle sentence.",
      "Not big.",
      "Just a small truth."
    ],
      [
      "Today I choose a gentle sentence.",
      "Not big.",
      "Just a small truth."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "One breath."
    ],
      [
      "I pause for a moment.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What would I like to hear right now?",
      "How does it sound when it is said to me?",
      "Can I say that to myself?"
    ],
      [
      "What would I like to hear right now?",
      "How does it sound when it is said to me?",
      "Can I say that to myself?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "A gentle sentence can be a lifeline."
    ],
      [
      "A gentle sentence can be a lifeline."
    ]
    ),
    action: localizedGenderNode(
      "say the sentence once.",
      "say the sentence once."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Return sentence",
    opening: localizedGenderNode(
      [
      "Today I choose a sentence that brings me back to presence.",
      "When I feel emptied.",
      "A small sentence."
    ],
      [
      "Today I choose a sentence that brings me back to presence.",
      "When I feel emptied.",
      "A small sentence."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe.",
      "I am here."
    ],
      [
      "I breathe.",
      "I am here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What did I learn about this emptiness?",
      "What is the first sign it returns?",
      "Which sentence brings me back a moment before?"
    ],
      [
      "What did I learn about this emptiness?",
      "What is the first sign it returns?",
      "Which sentence brings me back a moment before?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "I can meet the emptiness and breathe inside it."
    ],
      [
      "I can meet the emptiness and breathe inside it."
    ]
    ),
    action: localizedGenderNode(
      "say the new sentence once.",
      "say the new sentence once."
    ),
  },
].map(buildNormalizedDay);

export const everythingIsFineOnPaperButInsideIsEmptyBlueprintEn = buildNormalizedPilotBlueprint({
  id: "everything_is_fine_on_paper_but_inside_is_empty_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "everything_is_fine_on_paper_but_inside_is_empty",
  category: "depression_emptiness_meaning",
  journeyName: {
    he: "הכול בסדר על הנייר - ובפנים ריק",
    en: "Everything is fine on paper - but inside is empty",
    es: "Todo está bien en el papel, pero por dentro está vacío",
  },
  days,
});

export default everythingIsFineOnPaperButInsideIsEmptyBlueprintEn;
